import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Row,
  Col,
  Card
} from "react-bootstrap";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaExclamationTriangle,
  FaFolder
} from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../constants";
import { theme } from "../styles/theme";

export default function CategoriesList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    categorie_name: "",
    description: ""
  });
  const [validationError, setValidationError] = useState({});

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${BASE_URL}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Erreur lors du chargement des catégories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCategories = categories.filter((category) =>
    category.categorie_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleShowModal = (category = null) => {
    if (category) {
      setCurrentCategory({
        id: category.id,
        categorie_name: category.categorie_name,
        description: category.description || ""
      });
    } else {
      setCurrentCategory({
        id: null,
        categorie_name: "",
        description: ""
      });
    }
    setValidationError({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentCategory({
      id: null,
      categorie_name: "",
      description: ""
    });
    setValidationError({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when user types
    if (validationError[name]) {
      setValidationError(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentCategory.categorie_name.trim()) {
      errors.categorie_name = "Le nom de la catégorie est requis";
    }
    setValidationError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      const method = currentCategory.id ? 'put' : 'post';
      const url = currentCategory.id 
        ? `${BASE_URL}/categories/${currentCategory.id}`
        : `${BASE_URL}/categories`;

      await axios[method](
        url,
        currentCategory,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: `Catégorie ${currentCategory.id ? 'mise à jour' : 'ajoutée'} avec succès`,
        timer: 1500,
        showConfirmButton: false
      });

      fetchCategories();
      handleCloseModal();
    } catch (err) {
      console.error("Error saving category:", err);
      const errorMessage = err.response?.data?.message || 'Une erreur est survenue lors de la sauvegarde de la catégorie';
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: errorMessage
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Êtes-vous sûr ?',
        text: "Cette action est irréversible !",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.colors.danger,
        cancelButtonColor: theme.colors.muted,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler'
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem("auth_token");
        await axios.delete(`${BASE_URL}/categories/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire(
          'Supprimé !',
          'La catégorie a été supprimée avec succès.',
          'success'
        );

        fetchCategories();
      }
    } catch (err) {
      console.error("Error deleting category:", err);
      const errorMessage = err.response?.data?.message || 'Une erreur est survenue lors de la suppression de la catégorie';
      Swal.fire(
        'Erreur',
        errorMessage,
        'error'
      );
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        <FaExclamationTriangle className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <div className="categories-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Gestion des catégories</h2>
        <Button 
          variant="primary" 
          onClick={() => handleShowModal()}
          className="d-flex align-items-center"
        >
          <FaPlus className="me-2" />
          Nouvelle catégorie
        </Button>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-3">
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '25%' }}>Nom</th>
              <th style={{ width: '55%' }}>Description</th>
              <th style={{ width: '15%' }} className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category, index) => (
                <tr key={category.id}>
                  <td>{index + 1}</td>
                  <td className="fw-semibold">
                    <div className="d-flex align-items-center">
                      <FaFolder className="me-2" style={{ color: theme.colors.primary }} />
                      {category.categorie_name}
                    </div>
                  </td>
                  <td>
                    {category.description || (
                      <span className="text-muted fst-italic">Aucune description</span>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(category)}
                      title="Modifier"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(category.id)}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4 text-muted">
                  Aucune catégorie trouvée
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal d'ajout/édition */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaFolder className="me-2" />
              {currentCategory.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nom de la catégorie <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="categorie_name"
                value={currentCategory.categorie_name}
                onChange={handleInputChange}
                isInvalid={!!validationError.categorie_name}
                placeholder="Ex: Problème technique"
              />
              {validationError.categorie_name && (
                <Form.Control.Feedback type="invalid">
                  {validationError.categorie_name}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={currentCategory.description}
                onChange={handleInputChange}
                placeholder="Description détaillée de la catégorie (optionnel)"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {currentCategory.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}